import * as React from 'react'
import {Box, Typography} from '@mui/material'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, PRIMARY_BLUE, withOpacity} from '../../../../styles/foundation/colors'

/**
 * Shared empty-state primitive for Weight Freeze (v2) — MUI has no
 * built-in equivalent. Modelled directly on `disputes-v2/EmptyState.js`:
 * the same tinted icon circle, bold headline, one line of subtle body
 * copy, and an optional action underneath, so the two modules read as
 * one product rather than two.
 *
 * Disputes only ever needs two fixed situations ("nothing in this tab"
 * vs "filters over-narrowed a tab that does have rows"), so it hardcodes
 * both copy blocks behind a `variant` switch. Freeze hits an empty state
 * for a different reason on every one of its six sections — no SKUs
 * mapped yet, no DWS evidence on file, no requests raised, a filtered
 * table with nothing matching — and the copy shares nothing in common
 * across them. Rather than growing a `variant` enum per caller, this
 * version takes the icon, headline, body and action directly; each
 * section owns its own copy instead of this component owning all of it.
 */
export default function EmptyState({icon: Icon = InboxOutlinedIcon, title, body, action}) {
  return (
    <Box sx={{p: 5, textAlign: 'center'}}>
      <Box sx={{
        width: 48, height: 48, borderRadius: '10px', mx: 'auto', mb: 1.5,
        bgcolor: withOpacity(PRIMARY_BLUE, 0.12), color: PRIMARY_BLUE,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon />
      </Box>
      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY}}>
        {title}
      </Typography>
      {body && (
        <Typography sx={{fontFamily: FONT, color: SUBTLE, fontSize: SIZE['2xs'], mt: 0.5, maxWidth: 360, mx: 'auto'}}>
          {body}
        </Typography>
      )}
      {action && <Box sx={{mt: 1.5}}>{action}</Box>}
    </Box>
  )
}
